import { createNestablePublicClientApplication } from '@azure/msal-browser'

/**
 * MSAL.js Singleton für FlawDesk for Outlook (NAA).
 *
 * Verwendet `createNestablePublicClientApplication` (v5-Factory) — das ist
 * der von Microsoft empfohlene Weg für Office-Add-ins. Wichtige Eigenschaft:
 * die Factory fällt automatisch auf einen Standard-PCA zurück, falls der
 * Office-Host keinen NAA-Bridge bietet. D.h. dieses Modul funktioniert
 * sowohl im Outlook-iframe (silent SSO) als auch im Standalone-Browser
 * (loginPopup → User sieht das Microsoft-Login).
 *
 * Singleton-Pattern: `createNestablePublicClientApplication` ist eine
 * async Factory; wir cachen die Instance in einem Modul-State, damit
 * mehrfache Aufrufe nur eine PCA-Instance erzeugen. Erster Caller startet
 * die Initialisierung, alle weiteren bekommen das gleiche Promise.
 */

const CLIENT_ID = import.meta.env.VITE_ENTRA_CLIENT_ID
const AUTHORITY = import.meta.env.VITE_ENTRA_AUTHORITY ||
  'https://login.microsoftonline.com/common'
const AUDIENCE = import.meta.env.VITE_ENTRA_AUDIENCE

if (!CLIENT_ID) {
  // Werfen wir bewusst nicht — das Modul soll auch in Tests ohne .env
  // importierbar bleiben. Aber wir warnen klar, damit der Fehler beim
  // ersten acquireToken() nicht wie ein MSAL-Bug aussieht.
  // eslint-disable-next-line no-console
  console.error(
    '[auth] VITE_ENTRA_CLIENT_ID ist nicht gesetzt. ' +
      'Authentifizierung wird fehlschlagen.',
  )
}

// Default-Scope, mit dem das Add-in ein Token fuer seine eigene API holt.
// #1534: Self-Token (App fragt ein Token fuer die EIGENE exponierte API an)
// wird vom WAM-Broker mit AADSTS90009 abgelehnt, wenn die Resource als
// App-ID-URI angegeben wird -- selbst die GUID-URI-Form api://<clientId>
// reicht NICHT. Der Broker akzeptiert fuer den Self-Token nur die BLANKE
// Client-ID-GUID als Resource. Daher <clientId>/access_as_user statt
// api://<clientId>/access_as_user. (VITE_ENTRA_AUDIENCE bleibt fuer Manifest
// und Backend-aud relevant, aber NICHT fuer den MSAL-Scope.)
export const DEFAULT_SCOPES = [`${CLIENT_ID}/access_as_user`]

let pcaPromise = null

/**
 * Liefert die initialisierte PCA-Instance (cached).
 * Ist ab dem 2. Aufruf praktisch instant.
 */
export function getMsal() {
  if (!pcaPromise) {
    pcaPromise = createNestablePublicClientApplication({
      auth: {
        clientId: CLIENT_ID,
        authority: AUTHORITY,
      },
      cache: {
        // localStorage übersteht Page-Reloads (Office lädt das Add-in
        // beim Wechsel des Mail-Items teilweise neu). sessionStorage
        // wäre sicherer, aber dann müsste jeder Mail-Wechsel neu
        // authentifizieren.
        cacheLocation: 'localStorage',
      },
    })
  }
  return pcaPromise
}

/**
 * Holt ein Access-Token für die FlawDesk-API.
 *
 * Reihenfolge:
 * 1. acquireTokenSilent — funktioniert in Outlook (NAA) und für
 *    bereits authentifizierte Standalone-Browser-Sessions.
 * 2. Fallback acquireTokenPopup — Standalone-Mode oder wenn das
 *    Silent-Token abgelaufen ist und nicht silent erneuert werden kann.
 *
 * Im echten Outlook-Add-in-Kontext greift fast immer Schritt 1.
 */
export async function acquireToken({ scopes = DEFAULT_SCOPES } = {}) {
  const pca = await getMsal()

  // Wenn schon ein Account bekannt ist (z.B. nach Reload), setzen wir
  // ihn aktiv, damit acquireTokenSilent ihn nutzt.
  const accounts = pca.getAllAccounts()
  if (accounts.length > 0 && !pca.getActiveAccount()) {
    pca.setActiveAccount(accounts[0])
  }

  try {
    return await pca.acquireTokenSilent({ scopes })
  } catch (silentErr) {
    // Silent-Fehler sind in NAA-Kontexten oft "noch nie eingeloggt".
    // Popup öffnet im Standalone-Mode den Microsoft-Login;
    // im Outlook-Add-in fällt die NAA-Implementation auf einen
    // System-Dialog zurück, der seamless in der Host-App läuft.
    return await pca.acquireTokenPopup({ scopes })
  }
}

/**
 * Liefert die aktuell signierte Account-Info (oder null).
 * Nützlich, um Name/E-Mail im Taskpane anzuzeigen ohne ein zweites
 * Token-Acquire-Roundtrip.
 */
export async function getActiveAccount() {
  const pca = await getMsal()
  return pca.getActiveAccount() ?? pca.getAllAccounts()[0] ?? null
}
